import { useCallback, useEffect, useState } from 'react';
import {
  getNotificationPermission,
  isNative,
  openAppSettings,
  requestNotificationPermission,
  sendTestNotification,
} from '../lib/nativeBridge';
import { usePushNotifications } from './usePushNotifications';

export interface NotificationSetting {
  supported: boolean;
  on: boolean;
  busy: boolean;
  /** Set when asking was refused: the phone's settings are the way back. */
  blocked: boolean;
  setOn: (on: boolean) => Promise<void>;
  sendTest: () => void;
}

/**
 * Push notifications on or off, the same switch everywhere. In a wrapper the
 * phone owns the permission and OneSignal the subscription, so on asks the
 * phone and off opens its settings, where it is turned off. In a browser it
 * is the app's own web push: on subscribes and off really unsubscribes.
 */
export function useNotificationSetting(): NotificationSetting {
  const native = isNative();
  const web = usePushNotifications();
  const [granted, setGranted] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [asking, setAsking] = useState(false);

  useEffect(() => {
    if (!native) return;
    let current = true;
    getNotificationPermission().then((answer) => {
      if (current) setGranted(answer);
    });
    return () => {
      current = false;
    };
  }, [native]);

  const setOn = useCallback(
    async (on: boolean) => {
      if (native) {
        if (!on) {
          openAppSettings();
          return;
        }
        setAsking(true);
        const answer = await requestNotificationPermission();
        setAsking(false);
        setGranted(answer);
        setBlocked(!answer);
        return;
      }
      const done = on
        ? await web.actions.subscribe()
        : await web.actions.unsubscribe();
      setBlocked(on && !done && web.permission === 'denied');
    },
    [native, web.actions, web.permission]
  );

  if (native)
    return {
      supported: true,
      on: granted,
      busy: asking,
      blocked,
      setOn,
      sendTest: sendTestNotification,
    };

  return {
    supported: web.isSupported,
    on: web.isSubscribed,
    busy: web.isLoading,
    blocked: blocked || web.permission === 'denied',
    setOn,
    sendTest: () => void web.actions.sendTestNotification(),
  };
}
